import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { tokenService } from '../services/tokenService';
import { API_URL } from '../../utils/config';

interface OTPUser {
  id: string;
  email: string;
  name?: string;
  authMethod: 'otp';
  roles: string[];
  permissions: string[];
}

interface OTPAuthContextType {
  isAuthenticated: boolean;
  user: OTPUser | null;
  loading: boolean;
  error: string | null;
  requestOTP: (email: string) => Promise<{ success: boolean; message: string }>;
  verifyOTP: (email: string, otpCode: string) => Promise<{ success: boolean; error?: string }>;
  logout: (logoutAll?: boolean) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  clearError: () => void;
}

const OTPAuthContext = createContext<OTPAuthContextType | undefined>(undefined);

export const useOTPAuth = () => {
  const context = useContext(OTPAuthContext);
  if (!context) {
    throw new Error('useOTPAuth must be used within OTPAuthProvider');
  }
  return context;
};

interface OTPAuthProviderProps {
  children: React.ReactNode;
}

export const OTPAuthProvider: React.FC<OTPAuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<OTPUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestOTP = useCallback(async (email: string) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/otp/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          // Rate limited
          const retryAfter = data.retryAfterSeconds || 60;
          setError(`Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`);
          return { 
            success: false, 
            message: `Too many requests. Please try again in ${Math.ceil(retryAfter / 60)} minutes.` 
          };
        }
        throw new Error(data.message || 'Failed to request OTP');
      }

      return { success: true, message: data.message };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to request OTP';
      setError(message);
      return { success: false, message };
    }
  }, []);

  const verifyOTP = useCallback(async (email: string, otpCode: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/auth/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otpCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify OTP');
      }

      // Store tokens
      tokenService.setTokens(data.accessToken, data.refreshToken);
      
      // Store session ID if provided
      if (data.sessionId) {
        sessionStorage.setItem('sessionId', data.sessionId);
      }

      // Set user state
      setUser(data.user);
      setIsAuthenticated(true);

      // Setup automatic token refresh
      setupTokenRefresh();

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to verify OTP';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshTokenValue = tokenService.getRefreshToken();
      
      if (!refreshTokenValue) {
        return false;
      }

      const response = await fetch(`${API_URL}/api/auth/token/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to refresh token');
      }

      // Update access token
      tokenService.setAccessToken(data.accessToken);
      
      // Update user state if provided
      if (data.user) {
        setUser(data.user);
      }

      return true;
    } catch (err) {
      console.error('Failed to refresh token:', err);
      setIsAuthenticated(false);
      setUser(null);
      tokenService.clearTokens();
      return false;
    }
  }, []);

  const logout = useCallback(async (logoutAll = false) => {
    try {
      setLoading(true);
      const accessToken = tokenService.getAccessToken();
      const refreshTokenValue = tokenService.getRefreshToken();
      
      if (accessToken) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            refreshToken: refreshTokenValue,
            logoutAll,
          }),
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local state regardless of API call success
      setIsAuthenticated(false);
      setUser(null);
      tokenService.clearTokens();
      
      // Clear ALL browser storage data
      localStorage.clear();
      sessionStorage.clear();
      
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Setup automatic token refresh
  const setupTokenRefresh = useCallback(() => {
    // Clear any existing refresh timer
    const existingTimer = sessionStorage.getItem('refreshTimerId');
    if (existingTimer) {
      clearTimeout(parseInt(existingTimer));
    }

    // Refresh token 1 minute before expiry
    const accessToken = tokenService.getAccessToken();
    if (accessToken) {
      const payload = tokenService.decodeToken(accessToken);
      if (payload && payload.exp) {
        const expiryTime = payload.exp * 1000;
        const currentTime = Date.now();
        const refreshTime = expiryTime - currentTime - 60000; // 1 minute before expiry
        
        if (refreshTime > 0) {
          const timerId = setTimeout(async () => {
            const success = await refreshToken();
            if (success) {
              setupTokenRefresh(); // Setup next refresh
            }
          }, refreshTime);
          
          sessionStorage.setItem('refreshTimerId', timerId.toString());
        }
      }
    }
  }, [refreshToken]);

  // Clean up refresh timer on unmount
  useEffect(() => {
    return () => {
      const timerId = sessionStorage.getItem('refreshTimerId');
      if (timerId) {
        clearTimeout(parseInt(timerId));
        sessionStorage.removeItem('refreshTimerId');
      }
    };
  }, []);

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);
        const token = tokenService.getAccessToken();
        
        if (token) {
          const payload = tokenService.decodeToken(token);
          
          if (payload && payload.auth_method === 'otp') {
            // Check if token is expired
            if (!tokenService.isTokenExpired(token)) {
              setUser({
                id: payload.sub,
                email: payload.email,
                name: payload.name,
                authMethod: 'otp',
                roles: payload.roles || [],
                permissions: payload.permissions || [],
              });
              setIsAuthenticated(true);
            } else {
              // Try to refresh token
              const refreshTokenValue = tokenService.getRefreshToken();
              if (refreshTokenValue) {
                const refreshSuccess = await refreshToken();
                if (!refreshSuccess) {
                  // Clear expired tokens
                  tokenService.clearTokens();
                }
              } else {
                tokenService.clearTokens();
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to initialize OTP auth:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [refreshToken]);

  const value: OTPAuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    requestOTP,
    verifyOTP,
    logout,
    refreshToken,
    clearError,
  };

  return (
    <OTPAuthContext.Provider value={value}>
      {children}
    </OTPAuthContext.Provider>
  );
};